import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import api, { getApiBaseUrl } from '../lib/api';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';
import { FileText, Download, Eye, Upload, FileSignature, Send, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API_URL = getApiBaseUrl();

export default function Documents() {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [tenancies, setTenancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [showSendForSigning, setShowSendForSigning] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadType, setUploadType] = useState('');
  const [selectedTenancy, setSelectedTenancy] = useState('');
  const [signingInProgress, setSigningInProgress] = useState(false);
  const isLandlord = user?.role === 'landlord' || user?.role === 'landlord_admin';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [docsRes, tenanciesRes] = await Promise.all([
        api.get('/documents'),
        api.get('/tenancies')
      ]);
      setDocuments(docsRes.data || []);
      setTenancies(tenanciesRes.data || []);
    } catch (error) {
      console.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  const loadDocuments = loadData;

  const sendForSigning = async () => {
    if (!selectedTenancy) {
      toast.error('Please select a tenancy');
      return;
    }
    
    setSigningInProgress(true);
    try {
      const { data } = await api.post(`/tenancy/${selectedTenancy}/send-for-signing`);
      if (data.envelope_id) {
        toast.success('Agreement sent for signing via DocuSign');
        setShowSendForSigning(false);
        loadData();
      } else if (data.error) {
        toast.error(data.error);
      }
    } catch (error) {
      toast.error('Failed to send for signing');
    } finally {
      setSigningInProgress(false);
    }
  };

  const getDocumentIcon = (type) => {
    switch (type) {
      case 'tenancy_agreement': return <FileSignature className="w-5 h-5 text-purple-600" />;
      case 'epc': return <FileText className="w-5 h-5 text-green-600" />;
      case 'gas_safety': return <FileText className="w-5 h-5 text-orange-600" />;
      default: return <FileText className="w-5 h-5 text-primary" />;
    }
  };

  const getStatusBadge = (doc) => {
    if (doc.signed) {
      return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Signed</span>;
    }
    if (doc.pending_signature) {
      return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium flex items-center gap-1"><Clock className="w-3 h-3" /> Awaiting Signature</span>;
    }
    return null;
  };

  const activeTenancies = tenancies.filter(t => t.status === 'active' && !t.agreement_signed);

  return (
    <DashboardLayout>
      <div className="max-w-6xl">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-display font-bold text-slate-900 mb-2">Documents</h1>
            <p className="text-lg text-slate-600">Access and manage your tenancy documents</p>
          </div>
          <div className="flex gap-3">
            {isLandlord && (
              <Button onClick={() => setShowSendForSigning(true)} variant="default">
                <FileSignature className="w-5 h-5 mr-2" />
                Send for E-Sign
              </Button>
            )}
            <Button onClick={() => setShowUpload(true)} variant="outline">
              <Upload className="w-5 h-5 mr-2" />
              Upload
            </Button>
          </div>
        </div>

        {/* Quick Actions for Landlords */}
        {isLandlord && activeTenancies.length > 0 && (
          <Card className="p-6 mb-8 bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <FileSignature className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-purple-900">E-Signature Ready</h3>
                  <p className="text-purple-700 text-sm">{activeTenancies.length} tenancies awaiting agreement signing</p>
                </div>
              </div>
              <Button onClick={() => setShowSendForSigning(true)}>
                <Send className="w-4 h-4 mr-2" />
                Send via DocuSign
              </Button>
            </div>
          </Card>
        )}

        {/* Document Categories */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="p-6 text-center">
            <div className="p-4 bg-purple-100 rounded-full w-fit mx-auto mb-4">
              <FileSignature className="w-8 h-8 text-purple-600" />
            </div>
            <h3 className="font-semibold mb-1">Tenancy Agreements</h3>
            <p className="text-sm text-slate-600">{documents.filter(d => d.document_type === 'tenancy_agreement').length} documents</p>
          </Card>
          
          <Card className="p-6 text-center">
            <div className="p-4 bg-green-100 rounded-full w-fit mx-auto mb-4">
              <FileText className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="font-semibold mb-1">Certificates</h3>
            <p className="text-sm text-slate-600">{documents.filter(d => ['epc', 'gas_safety', 'eicr'].includes(d.document_type)).length} documents</p>
          </Card>
          
          <Card className="p-6 text-center">
            <div className="p-4 bg-blue-100 rounded-full w-fit mx-auto mb-4">
              <FileText className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="font-semibold mb-1">Other Documents</h3>
            <p className="text-sm text-slate-600">{documents.filter(d => !['tenancy_agreement', 'epc', 'gas_safety', 'eicr'].includes(d.document_type)).length} documents</p>
          </Card>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading documents...</div>
        ) : documents.length === 0 ? (
          <Card className="p-12 text-center">
            <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold mb-2">No documents yet</h3>
            <p className="text-slate-600 mb-6">Upload documents or send agreements for signing</p>
            {isLandlord && (
              <Button onClick={() => setShowSendForSigning(true)}>
                <FileSignature className="w-4 h-4 mr-2" />
                Send Agreement for E-Sign
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {documents.map((doc) => (
              <Card key={doc.id} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    {getDocumentIcon(doc.document_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-1">
                      <h3 className="font-semibold truncate">{doc.name}</h3>
                      {getStatusBadge(doc)}
                    </div>
                    <p className="text-sm text-slate-600 mb-3">
                      {doc.document_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} • {formatDate(doc.created_at)}
                    </p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline">
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      <Button size="sm" variant="outline">
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Send for E-Signing Dialog */}
        <Dialog open={showSendForSigning} onOpenChange={setShowSendForSigning}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>E-Signature Integration</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="p-6 bg-amber-50 border border-amber-200 rounded-lg text-center">
                <FileSignature className="w-12 h-12 text-amber-600 mx-auto mb-3" />
                <h3 className="font-semibold text-amber-900 mb-2">Coming Soon</h3>
                <p className="text-sm text-amber-700 mb-4">
                  Electronic signature integration with DocuSign is currently being configured. 
                  This feature will allow tenants and landlords to sign tenancy agreements digitally.
                </p>
                <p className="text-xs text-amber-600">
                  In the meantime, you can download the agreement and sign manually.
                </p>
              </div>
              
              <div className="flex justify-end pt-2">
                <Button type="button" variant="outline" onClick={() => setShowSendForSigning(false)}>
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Upload Dialog */}
        <Dialog open={showUpload} onOpenChange={setShowUpload}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Upload Document</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Document Type</Label>
                <Select value={uploadType} onValueChange={setUploadType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tenancy_agreement">Tenancy Agreement</SelectItem>
                    <SelectItem value="gas_safety">Gas Safety Certificate</SelectItem>
                    <SelectItem value="epc">EPC</SelectItem>
                    <SelectItem value="eicr">EICR</SelectItem>
                    <SelectItem value="inventory">Inventory</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>File</Label>
                <Input 
                  type="file" 
                  accept=".pdf,.doc,.docx,.jpg,.png" 
                  onChange={(e) => setUploadFile(e.target.files[0])}
                />
              </div>

              <div className="flex gap-4 pt-4">
                <Button 
                  className="flex-1"
                  onClick={async () => {
                    if (!uploadFile || !uploadType) {
                      alert('Please select a document type and file');
                      return;
                    }
                    
                    const formData = new FormData();
                    formData.append('file', uploadFile);
                    formData.append('document_type', uploadType);
                    
                    try {
                      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
                      const response = await fetch(`${API_URL}/api/files/upload`, {
                        method: 'POST',
                        headers: {
                          'Authorization': `Bearer ${token}`
                        },
                        body: formData
                      });
                      
                      if (response.ok) {
                        alert('Document uploaded successfully!');
                        setShowUpload(false);
                        setUploadFile(null);
                        setUploadType('');
                        loadDocuments();
                      } else {
                        alert('Upload failed. Please try again.');
                      }
                    } catch (error) {
                      console.error('Upload error:', error);
                      alert('Upload failed. Please try again.');
                    }
                  }}
                >
                  Upload
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowUpload(false)}>Cancel</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
